import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Badge, Row, Col, Alert, Spinner, Modal, Tabs, Tab, Container, Form, InputGroup } from 'react-bootstrap';
import { FaTrash, FaSearch, FaTimes } from 'react-icons/fa';
import dataService from '../services/dataService';
import { formatRelativeDate } from '../utils/dataUtils';
import RatingModal from './RatingModal';
import { useLanguage } from '../contexts/LanguageContext';
import { t, tf } from '../utils/i18n';
import WatchedContentCard from './WatchedContentCard';
import WatchedContentListGrid from './WatchedContentListGrid';
import { log as debugLog, error as logError } from '../utils/logger';

const getSortDate = (item) => item.watched_date || item.date_watched || item.listened_date || '';

const sortByDateDesc = (items) =>
  [...(items || [])].sort((a, b) => (getSortDate(b) || '').localeCompare(getSortDate(a) || ''));

const filterContent = (contentList, query) => {
  if (!query || query.trim() === '') return contentList;

  const searchTerm = query.toLowerCase().trim();
  return contentList.filter((item) => {
    const titleMatch = item.title?.toLowerCase().includes(searchTerm);
    const artistMatch = item.artist?.toLowerCase().includes(searchTerm);
    const directorMatch = item.director?.toLowerCase().includes(searchTerm);
    const actorsMatch = item.actors?.some((actor) => actor.toLowerCase().includes(searchTerm));
    const genreMatch = (Array.isArray(item.genre) ? item.genre.join(' ') : String(item.genre || ''))
      .toLowerCase()
      .includes(searchTerm);
    const yearMatch = item.year?.toString().includes(searchTerm);

    return titleMatch || artistMatch || directorMatch || actorsMatch || genreMatch || yearMatch;
  });
};

const WatchedContentManager = ({ embedMode = false, contentType = 'movie' }) => {
  const { language } = useLanguage();
  const [watchedMovies, setWatchedMovies] = useState([]);
  const [watchedSeries, setWatchedSeries] = useState([]);
  const [listenedMusic, setListenedMusic] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('movies');
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contentToDelete, setContentToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [totals, setTotals] = useState({ movies: 0, series: 0, music: 0 });

  const loadType = useCallback(async (type) => {
    const res = await dataService.getWatchedPage(type, 1, { forSearch: true });
    return { items: sortByDateDesc(res.items), total: res.total ?? 0 };
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (embedMode) {
        const { items, total } = await loadType(contentType);
        if (contentType === 'movie') {
          setWatchedMovies(items);
          setTotals((prev) => ({ ...prev, movies: total }));
        } else if (contentType === 'series') {
          setWatchedSeries(items);
          setTotals((prev) => ({ ...prev, series: total }));
        } else {
          setListenedMusic(items);
          setTotals((prev) => ({ ...prev, music: total }));
        }
      } else {
        const [moviesRes, seriesRes, musicRes] = await Promise.all([
          loadType('movie'),
          loadType('series'),
          loadType('music'),
        ]);
        setWatchedMovies(moviesRes.items);
        setWatchedSeries(seriesRes.items);
        setListenedMusic(musicRes.items);
        setTotals({
          movies: moviesRes.total,
          series: seriesRes.total,
          music: musicRes.total,
        });
      }
    } catch (err) {
      setError(t('history.errorLoading', language));
      logError('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [embedMode, contentType, loadType, language]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  const handleEditRating = (content, type) => {
    setSelectedContent({ ...content, type });
    setShowRatingModal(true);
  };

  const handleDeleteContent = (content, type) => {
    setContentToDelete({ ...content, type });
    setShowDeleteModal(true);
  };

  const handleRatingUpdate = async (rating, notes, genreOverride) => {
    try {
      const updates = { user_rating: rating, notes: notes || '' };
      if (selectedContent.type === 'music' && genreOverride != null && String(genreOverride).trim()) {
        updates.genre = String(genreOverride).trim();
      }
      const success = await dataService.updateWatchedContent(
        selectedContent.type,
        selectedContent.id,
        updates
      );

      if (success) {
        await loadData();
        setShowRatingModal(false);
        setSelectedContent(null);
      }
    } catch (err) {
      logError('Error updating rating:', err);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      debugLog('Deleting content:', contentToDelete);
      const success = await dataService.removeWatchedContent(
        contentToDelete.type,
        contentToDelete.id
      );

      if (success) {
        await loadData();
        setShowDeleteModal(false);
        setContentToDelete(null);
      }
    } catch (err) {
      logError('Error deleting content:', err);
    }
  };

  const renderContentCard = (content, type) => {
    const dateField = type === 'music' ? 'listened_date' : 'watched_date';
    const dateLabel = type === 'music' ? t('history.listened', language) : t('history.watched', language);
    const dateValue = content[dateField] || content.date_watched || content.watched_date;
    const dateStr = formatRelativeDate(dateValue, language);
    const displayRating = content.user_rating ?? content.ratings?.user_rating ?? content.rating;

    return (
      <Col key={content.id} xs={12} sm={6} md={4} className="mb-4">
        <WatchedContentCard
          content={content}
          type={type}
          language={language}
          dateLabel={dateLabel}
          dateStr={dateStr}
          displayRating={displayRating}
          onEdit={handleEditRating}
          onDelete={handleDeleteContent}
        />
      </Col>
    );
  };

  const renderContentList = (type) => {
    const rawList =
      type === 'movie' ? watchedMovies : type === 'series' ? watchedSeries : listenedMusic;
    const contentList = filterContent(rawList, searchQuery);

    if (contentList.length === 0) {
      if (searchQuery.trim()) {
        return (
          <Alert variant="warning">
            <Alert.Heading>{tf('history.noResults', { query: searchQuery }, language)}</Alert.Heading>
            <p>
              <Button variant="link" className="p-0" onClick={() => handleSearchChange('')}>
                {t('history.clearSearch', language)}
              </Button>
            </p>
          </Alert>
        );
      }

      return (
        <Alert variant="info">
          <Alert.Heading>
            {tf(
              'history.noContentWatched',
              {
                type:
                  type === 'music'
                    ? t('history.tabs.music', language).toLowerCase()
                    : type === 'movie'
                      ? t('history.tabs.movies', language).toLowerCase()
                      : t('history.tabs.series', language).toLowerCase(),
              },
              language
            )}
          </Alert.Heading>
          <p>
            {tf(
              'history.whenYouMark',
              {
                type:
                  type === 'music'
                    ? t('history.listened', language).toLowerCase()
                    : t('history.watched', language).toLowerCase(),
              },
              language
            )}
          </p>
        </Alert>
      );
    }

    return (
      <WatchedContentListGrid
        items={contentList}
        renderCard={(content) => renderContentCard(content, type)}
      />
    );
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t('common.loading', language)}</span>
        </Spinner>
        <p className="mt-3">{t('history.loading', language)}</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>{t('common.error', language)}</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" onClick={loadData}>
          {t('history.retry', language)}
        </Button>
      </Alert>
    );
  }

  const totalWatched = totals.movies + totals.series + totals.music;
  const typeTotal =
    contentType === 'movie' ? totals.movies : contentType === 'series' ? totals.series : totals.music;

  const searchBlock = (
    <div className="mb-3">
      <InputGroup>
        <InputGroup.Text>
          <FaSearch />
        </InputGroup.Text>
        <Form.Control
          type="text"
          placeholder={t('history.searchPlaceholder', language)}
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        {searchQuery && (
          <Button
            variant="outline-secondary"
            onClick={() => handleSearchChange('')}
            title={t('history.clearSearch', language)}
          >
            <FaTimes />
          </Button>
        )}
      </InputGroup>
    </div>
  );

  const modalsBlock = (
    <>
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('history.confirmDeleteTitle', language)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{tf('history.confirmDeleteMessage', { title: contentToDelete?.title || '' }, language)}</p>
          <div className="alert alert-info">
            <strong>ℹ️ {t('history.deleteNote', language)}:</strong>{' '}
            {tf(
              'history.deleteNoteInfo',
              {
                rating:
                  (contentToDelete?.user_rating ?? contentToDelete?.ratings?.user_rating) != null
                    ? `${contentToDelete?.user_rating ?? contentToDelete?.ratings?.user_rating}/10`
                    : '—',
              },
              language
            )}
          </div>
          <p className="text-muted small">{t('history.actionCannotBeUndone', language)}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            {t('common.cancel', language)}
          </Button>
          <Button variant="warning" onClick={handleConfirmDelete}>
            <FaTrash className="me-1" />
            {t('history.deleteAndReturn', language)}
          </Button>
        </Modal.Footer>
      </Modal>

      <RatingModal
        show={showRatingModal}
        onHide={() => setShowRatingModal(false)}
        onSubmit={handleRatingUpdate}
        content={selectedContent}
        type={selectedContent?.type}
        isEdit
      />
    </>
  );

  if (embedMode) {
    const shownCount = filterContent(
      contentType === 'movie' ? watchedMovies : contentType === 'series' ? watchedSeries : listenedMusic,
      searchQuery
    ).length;

    return (
      <div className="watched-content-embedded">
        {searchBlock}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <h5 className="mb-0">
            {t('history.title', language)} ({searchQuery.trim() ? shownCount : typeTotal})
          </h5>
        </div>
        {renderContentList(contentType)}
        {modalsBlock}
      </div>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="mb-4">
        <h1 className="text-center mb-3">📚 {t('history.title', language)}</h1>
        <p className="text-center text-muted">{t('history.manageHistory', language)}</p>
        <div className="text-center mb-3">
          <Badge bg="info" className="fs-6">
            {tf('history.totalElements', { count: totalWatched }, language)}
          </Badge>
        </div>
        {searchBlock}
      </div>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4" justify>
        <Tab
          eventKey="movies"
          title={`🎬 ${t('history.tabs.movies', language)} (${searchQuery ? `${filterContent(watchedMovies, searchQuery).length}/${totals.movies}` : totals.movies})`}
        >
          {renderContentList('movie')}
        </Tab>
        <Tab
          eventKey="series"
          title={`📺 ${t('history.tabs.series', language)} (${searchQuery ? `${filterContent(watchedSeries, searchQuery).length}/${totals.series}` : totals.series})`}
        >
          {renderContentList('series')}
        </Tab>
        <Tab
          eventKey="music"
          title={`🎵 ${t('history.tabs.music', language)} (${searchQuery ? `${filterContent(listenedMusic, searchQuery).length}/${totals.music}` : totals.music})`}
        >
          {renderContentList('music')}
        </Tab>
      </Tabs>

      {modalsBlock}
    </Container>
  );
};

export default WatchedContentManager;
